import React, { useEffect, useState } from "react";
import { useWebTranslation } from "../../../../translations";
import Gutter from "../../../../ui-kit/Gutter";
import ChatMessage, {
  ChatContainer,
} from "../../../../ui-kit/ChatMessage/ChatMessage";
import useBreeds, { Breed } from "../../../../hooks/useBreeds";
import { SpeciesEnum } from "../../../../../__generated__/globalTypes";
import { isDefined } from "../../../../utils/utils";
import { Typography } from "../../../../ui-kit/Typography";
import { FlexRow } from "../../../../ui-kit/Layout/Flex";
import { useCurrentInsuranceByAnimalId } from "../../store/selectors/insurance";
import { StepProps } from "../types";
import { useDispatch } from "react-redux";
import { updateAnimalById } from "components/Signup/store/slices/animals";
import FlowButtons from "components/Signup/components/FlowButtons/FlowButtons";
import { useCurrentAnimal } from "components/Signup/store/selectors/animals";
import { MinimalBreedInfo } from "components/Signup/store/state/types";
import { SelectorSearch } from "components/Signup/components/SelectorSearch";
import { BreedEmoji } from "components/BreedEmoji";
import { isOrg } from "../../../../utils/site";

const TOP_BREEDS_DOG = "DogBreed-blandras";
const TOP_BREEDS_CAT = "CatBreed-blandrashuskatt";
const EXCLUDE_BREEDS =
  "DogBreed-mixed-breed-low-risk,DogBreed-mixed-breed-high-risk";

const searchBreedByName = (breeds: Breed[], searchTerm: string) => {
  return breeds
    ?.filter(isDefined)
    .find((breed) => breed.name?.toLowerCase() === searchTerm.toLowerCase());
};

const AnimalBreed = ({ previous, next, isValid, transitioning }: StepProps) => {
  const { t } = useWebTranslation();
  const dispatch = useDispatch();
  const animal = useCurrentAnimal();
  const { insurely } = useCurrentInsuranceByAnimalId(animal.id);
  const [note, setNote] = useState<string>("");

  const { breeds: allBreeds } = useBreeds({ species: animal?.species });

  useEffect(() => {
    if (
      insurely?.shouldCompare &&
      insurely.currentInsurelyPolicyIndex !== undefined
    ) {
      const breedSearchTerm =
        insurely.policies?.[insurely.currentInsurelyPolicyIndex].insurance
          .animalBreed;

      if (breedSearchTerm) {
        const foundBreed = searchBreedByName(allBreeds, breedSearchTerm);

        if (foundBreed) {
          dispatch(
            updateAnimalById({ id: animal.id, update: { breed: foundBreed } })
          );
        }
      }
    }
  }, [allBreeds, insurely?.currentInsurelyPolicyIndex]);

  useEffect(() => {
    if (isOrg("fra")) {
      const breedIdsWithDisclaimer = [
        "DogBreed-americain-staffordshire-terrier",
        "DogBreed-staffordshire-terrier-americain",
        "DogBreed-tosa",
        "DogBreed-rottweiler",
        "DogBreed-rottweiler-miniature",
      ];

      const disclaimer = t("signup.animal_breed.disclaimer");

      if (
        animal.breed?.id &&
        breedIdsWithDisclaimer.includes(animal.breed.id)
      ) {
        setNote(disclaimer);
        return;
      }

      setNote("");
    }
  }, [animal.breed]);

  const { breeds, breedsLoading } = useBreeds({
    species: animal.species!,
    topBreeds:
      animal.species === SpeciesEnum.cat ? TOP_BREEDS_CAT : TOP_BREEDS_DOG,
    excludeBreeds: EXCLUDE_BREEDS,
  });

  return (
    <ChatContainer leaving={transitioning}>
      <ChatMessage>
        <FlexRow>
          <Typography>
            {t("signup.animal_breed.text", {
              name: animal.name ?? "",
            })}
          </Typography>
        </FlexRow>
        <Typography color="grey500">
          {t("signup.animal_breed.description_2", {
            species: animal?.species ?? "dog",
            mixed:
              animal.species === SpeciesEnum.cat
                ? t("signup.animal_breed.mixed_cat")
                : t("signup.animal_breed.mixed_dog"),
            name: animal.name ?? "",
          })}
        </Typography>
      </ChatMessage>

      <Gutter size={2} />
      <ChatMessage fromMe small>
        <SelectorSearch<MinimalBreedInfo>
          items={breeds}
          onSelectedItemChange={({ selectedItem }) => {
            dispatch(
              updateAnimalById({
                id: animal.id,
                update: { breed: selectedItem ?? undefined },
              })
            );
          }}
          placeholder={t("breed_selector.placeholder")}
          defaultSelectedItem={animal.breed}
          icon={<BreedEmoji />}
          itemToString={(item) => item?.name ?? ""}
          label={t("a11y.signup.breed.label")}
        />
      </ChatMessage>
      <Gutter size={2} />

      {note && (
        <div aria-live="polite" aria-atomic="true" role="alert">
          <ChatMessage index={6}>
            <Typography>{note}</Typography>
          </ChatMessage>
          <Gutter size={4} />
        </div>
      )}

      <FlowButtons
        inProgress={transitioning}
        backButton={false}
        next={next}
        disabled={!isValid}
        previous={previous}
      />
    </ChatContainer>
  );
};

export default AnimalBreed;
